import React, { useMemo } from 'react';
import styled from 'styled-components';
import type { VisualizationDefinition } from '@splunk/dashboard-types';
import { toPx } from '@splunk/dashboard-utils';
import VizProgressBar, { PROGRESSBAR_HEIGHT } from './VisualizationProgressBar';
import VizTitleAndDescription, {
    TITLE_CONTAINER_PADDING,
    TITLE_HEIGHT,
    DESCRIPTION_HEIGHT,
} from './VisualizationTitleAndDescription';
import VizStatusIcon from './VisualizationStatusIcon';
import { getIconStatus, sanitizeColor } from '../utils';
import type {
    DataSources,
    UseSubscribeToSearchesReturnType,
} from '../hooks/useSubscribeToSearches';

// Wrapper for just the Visualization
interface VizWrapperProps {
    width: string | number;
    height?: string | number;
}
interface ComputedProps {
    backgroundColor?: string;
}
const VizWrapper = styled.div.attrs<VizWrapperProps>(({ width, height }) => ({
    'data-test': 'viz-size-wrapper',
    style: {
        width: toPx(width),
        height: toPx(height),
    },
}))<VizWrapperProps>``;

const empty: Record<string, unknown> = {};

/**
 * Display Visualization and its sibling components (Progress, Title, Status)
 * @param {Object} props
 * @param {String} props.id The viz id
 * @param {Number} props.height The container height
 * @param {Number} props.width The container width
 * @param {Number} props.visualizationDefinition The viz config
 * @param {Boolean} props.isFullscreen Flag for fullscreen mode
 * @param {Object} props.computedProps Additional computed props from local page state
 * @param {React.ReactNode} props.children Content to show, either a viz or a placeholder
 */
export interface VisualizationContentProps
    extends Pick<UseSubscribeToSearchesReturnType, 'dataSources'> {
    id: string;
    width: number | string;
    height?: number | string;
    visualizationDefinition: VisualizationDefinition;
    isFullscreen: boolean;
    computedProps?: ComputedProps;
    children: JSX.Element | null;
}

const VisualizationContent = ({
    id,
    height,
    width,
    dataSources = empty as DataSources,
    visualizationDefinition: vizDef,
    isFullscreen,
    computedProps,
    children,
}: VisualizationContentProps): JSX.Element => {
    const {
        title,
        description,
        options = empty,
        showProgressBar = false,
        containerOptions,
    } = vizDef;
    const { backgroundColor } = options;
    let bgColor =
        computedProps?.backgroundColor || (backgroundColor as string) || null;
    if (bgColor) {
        bgColor = sanitizeColor(bgColor);
    }

    const hasStatusIcon = !!getIconStatus(dataSources.primary);
    const hasTitle = !!title || !!description;

    /**
     * Calculate the heights of all the components that are not the visualization
     * Used to tell the visualization how much room it has to use
     */
    const siblingHeights = useMemo(() => {
        let h = 0;

        if (showProgressBar) {
            // showProgressBar will enable progress bar
            h += PROGRESSBAR_HEIGHT;
        }

        if (hasTitle) {
            // TODO: there has to be a better way...
            h += title || description ? TITLE_CONTAINER_PADDING : 0;
            h += title ? TITLE_HEIGHT : 0;
            h += description ? DESCRIPTION_HEIGHT : 0;
        }

        return h;
    }, [showProgressBar, hasTitle, title, description]);

    const containerWidth = useMemo(
        () => (isFullscreen ? '100vw' : width),
        [isFullscreen, width]
    );

    const containerHeight = useMemo(
        () =>
            isFullscreen
                ? `calc(100vh - ${siblingHeights}px)`
                : Number(height) - siblingHeights || height,
        [isFullscreen, siblingHeights, height]
    );

    // if the height is not a number, and not in fullscreen, it means
    //  it's probably a percentage, ie. 100%. If it's a percent, we need to only
    //  subtract siblingHeights once, otherwise it will continuously get smaller as each
    //  child will do this calculation. Instead we do it once in the parent component
    const VizWrapperHeight =
        Number(containerHeight) || isFullscreen
            ? containerHeight
            : `calc(${height} - ${siblingHeights}px)`;

    let currentProgress = dataSources?.primary?.meta?.percentComplete ?? 0;
    if (dataSources?.primary?.error?.message) {
        currentProgress = 100;
    }

    const ChildrenWithDimensions = useMemo(
        () =>
            children &&
            React.cloneElement(children, {
                width: containerWidth,
                height: containerHeight,
            }),
        [children, containerWidth, containerHeight]
    );

    return (
        <>
            {showProgressBar && (
                <VizProgressBar
                    hasData={!!dataSources.primary}
                    percentage={currentProgress}
                    backgroundColor={bgColor}
                />
            )}
            <VizTitleAndDescription
                title={title}
                description={description}
                backgroundColor={bgColor}
                truncateFirstLine={hasStatusIcon}
                containerOptions={containerOptions}
            />
            <VizWrapper width={containerWidth} height={VizWrapperHeight}>
                {ChildrenWithDimensions}
            </VizWrapper>
            <VizStatusIcon dataSource={dataSources.primary} id={id} />
        </>
    );
};

export default VisualizationContent;
